import { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Loader } from 'lucide-react';
import { AuthProvider, useAuth } from './hooks/useAuth';
import Layout from './components/Layout';

const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Bots = lazy(() => import('./pages/Bots'));
const BotConfig = lazy(() => import('./pages/BotConfig'));
const Planos = lazy(() => import('./pages/Planos'));
const Checkout = lazy(() => import('./pages/Checkout'));
const Clientes = lazy(() => import('./pages/Clientes'));
const Equipe = lazy(() => import('./pages/Equipe'));
const Conversations = lazy(() => import('./pages/Conversations'));
const Conversas = lazy(() => import('./pages/Conversas'));
const Configuracoes = lazy(() => import('./pages/Configuracoes'));
const Agenda = lazy(() => import('./pages/Agenda'));
const Integracoes = lazy(() => import('./pages/Integracoes'));
const Perfil = lazy(() => import('./pages/Perfil'));
const Anamnese = lazy(() => import('./pages/Anamnese'));
const AnamneseRespostas = lazy(() => import('./pages/AnamneseRespostas'));
const AnamneseResposta = lazy(() => import('./pages/AnamneseResposta'));
const AnamnesePublic = lazy(() => import('./pages/AnamnesePublic'));
const Convite = lazy(() => import('./pages/Convite'));
const VerificarEmail = lazy(() => import('./pages/VerificarEmail'));
const EsqueciSenha = lazy(() => import('./pages/EsqueciSenha'));
const RedefinirSenha = lazy(() => import('./pages/RedefinirSenha'));

const PrivateRoute = ({ children }) => {
  const { token } = useAuth();
  return token ? children : <Navigate to="/login" replace />;
};

const PublicRoute = ({ children }) => {
  const { token } = useAuth();
  return !token ? children : <Navigate to="/dashboard" replace />;
};

const PageFallback = () => (
  <div className="flex items-center justify-center min-h-96">
    <Loader size={24} className="animate-spin text-jade-500" />
  </div>
);

export default function App() {
  return (
    <AuthProvider>
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
          <Route path="/verificar-email" element={<VerificarEmail />} />
          <Route path="/esqueci-senha" element={<PublicRoute><EsqueciSenha /></PublicRoute>} />
          <Route path="/redefinir-senha" element={<RedefinirSenha />} />
          <Route path="/formulario/:token" element={<AnamnesePublic />} />
          <Route path="/convite/:token" element={<Convite />} />
          {/* rota antiga, mantém links de fichas já enviados por WhatsApp funcionando */}
          <Route path="/anamnese/:token" element={<AnamnesePublic />} />
          <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="conversas" element={<Conversas />} />
            <Route path="agenda" element={<Agenda />} />
            <Route path="clientes" element={<Clientes />} />
            <Route path="bots" element={<Bots />} />
            <Route path="bots/:id" element={<BotConfig />} />
            <Route path="bots/:botId/conversations" element={<Conversations />} />
            <Route path="equipe" element={<Equipe />} />
            <Route path="integracoes" element={<Integracoes />} />
            <Route path="planos" element={<Planos />} />
            <Route path="planos/checkout/:planId" element={<Checkout />} />
            <Route path="configuracoes" element={<Configuracoes />} />
            <Route path="formularios" element={<Anamnese />} />
            <Route path="formularios/respostas" element={<AnamneseRespostas />} />
            <Route path="formularios/respostas/:id" element={<AnamneseResposta />} />
            <Route path="anamnese" element={<Navigate to="/formularios" replace />} />
            <Route path="anamnese/respostas" element={<Navigate to="/formularios/respostas" replace />} />
            <Route path="perfil" element={<Perfil />} />
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Suspense>
    </AuthProvider>
  );
}
